/**
 * [T-058] search syntax ฝั่งเว็บ — mirror กฎของ worker (apps/worker/src/tickets/search.ts)
 * คีย์ที่ backend รองรับจริง: status/team/tag/priority/from/service/id/has/is
 * คีย์ที่ไม่รองรับ (เช่น due:) → ไม่ส่งไป API แล้วแจ้งผู้ใช้ใต้ช่องค้นหา
 */
export type FilterToken = { key: string; value: string; raw: string };

/** คีย์ที่ worker parseSearch รู้จัก (ค่าของ has:/is: ตรวจที่ backend อีกชั้น) */
export const SUPPORTED_FILTER_KEYS = new Set(["status", "team", "tag", "priority", "from", "service", "id", "has", "is"]);

/** คีย์ที่แสดงเป็นชิปตัวกรองบนหัว inbox (ตามดีไซน์จอ 1) */
export const CHIP_FILTER_KEYS = new Set(["status", "team", "priority", "due"]);

export type ParsedQuery = { terms: string[]; filters: FilterToken[]; unsupported: FilterToken[] };

/** แยกโทเคนของ q — เคารพเครื่องหมายคำพูด ("…" เป็น term เดียว) เหมือน worker */
export function tokenizeQuery(q: string): string[] {
  const tokens: string[] = [];
  let cur = "";
  let has = false;
  let inQ = false;
  for (const ch of q) {
    if (inQ) {
      if (ch === '"') inQ = false;
      else cur += ch;
    } else if (ch === '"') {
      inQ = true;
      has = true;
    } else if (/\s/.test(ch)) {
      if (has) {
        tokens.push(cur);
        cur = "";
        has = false;
      }
    } else {
      cur += ch;
      has = true;
    }
  }
  if (has) tokens.push(cur);
  return tokens;
}

export function parseQuery(q: string): ParsedQuery {
  const terms: string[] = [];
  const filters: FilterToken[] = [];
  const unsupported: FilterToken[] = [];
  for (const raw of tokenizeQuery(q)) {
    const m = /^([a-zA-Z]+):(.*)$/.exec(raw);
    if (!m || m[2] === "") {
      if (raw !== "") terms.push(raw);
      continue;
    }
    const key = m[1]!.toLowerCase();
    const token: FilterToken = { key, value: m[2]!, raw };
    if (SUPPORTED_FILTER_KEYS.has(key)) filters.push(token);
    else unsupported.push(token);
  }
  return { terms, filters, unsupported };
}

/** q ที่จะส่งไป API จริง = terms + filters ที่รองรับ (ตัดคีย์ที่ backend ไม่รู้จักออก) */
export function toApiQuery(q: string): { apiQ: string | undefined; dropped: FilterToken[] } {
  const p = parseQuery(q);
  const parts = [...p.terms, ...p.filters.map((f) => f.raw)];
  const apiQ = parts.join(" ").trim();
  return { apiQ: apiQ === "" ? undefined : apiQ, dropped: p.unsupported };
}

/** ชิปตัวกรอง (status:/team:/priority:/due:) ที่อ่านจาก q ปัจจุบัน */
export function filterChips(q: string): FilterToken[] {
  const p = parseQuery(q);
  return [...p.filters, ...p.unsupported].filter((f) => CHIP_FILTER_KEYS.has(f.key));
}

/** ถอดชิปออกจาก q (ลบ raw token ตัวแรกที่ตรง) แล้วคืน q ใหม่ */
export function removeFilterToken(q: string, token: FilterToken): string {
  const tokens = tokenizeQuery(q);
  const idx = tokens.indexOf(token.raw);
  if (idx >= 0) tokens.splice(idx, 1);
  return tokens.join(" ");
}

/** ต่อ token ตัวกรองเข้า q (แทนคีย์เดิมถ้ามีค่าเดิมของ key เดียวกันอยู่) */
export function addFilterToken(q: string, key: string, value: string): string {
  const tokens = tokenizeQuery(q);
  const raw = `${key}:${value}`;
  if (!tokens.includes(raw)) tokens.push(raw);
  return tokens.join(" ");
}
